import {
  getStripeConfigApi,
  registerCardApi,
  getCardInfoApi,
  deleteCardApi,
  finalize3dsReturnApi,
  createCardSetupIntentApi,
  createGuestCardSetupIntentApi,
  getReceiptListApi,
  downloadReceiptApi,
} from '@/services/payment.service';
import constants from '@/shared/constants/constants';

const state = {
  stripePublishableKey: process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY || '',
  stripeReleaseDate: '',
  isStripe1DayApplicable: null,
  cardInfo: {
    has_card: false,
    card_brand: '',
    last4: '',
    exp_month: null,
    exp_year: null,
    card_status: '',
    is_1day_plan_active: false,
    has_payment_receipts: false,
  },
  loading: false,
  error: null,
};

const getters = {
  hasValidCard: (cardState) => (
    cardState.cardInfo.has_card
    && cardState.cardInfo.card_status === constants.CARD_STATUS_VALID
  ),
  publishableKey: (cardState) => cardState.stripePublishableKey,
  stripeReleaseDate: (cardState) => cardState.stripeReleaseDate,
  isStripe1DayApplicable: (cardState) => cardState.isStripe1DayApplicable,
  is1DayPlanActive: (cardState) => !!cardState.cardInfo.is_1day_plan_active,
  hasPaymentReceipts: (cardState) => !!cardState.cardInfo.has_payment_receipts,
};

const mutations = {
  SET_STRIPE_PUBLISHABLE_KEY(cardState, key) {
    cardState.stripePublishableKey = key || '';
  },
  SET_STRIPE_RELEASE_DATE(cardState, releaseDate) {
    cardState.stripeReleaseDate = releaseDate || '';
  },
  SET_IS_STRIPE_1DAY_APPLICABLE(cardState, applicable) {
    cardState.isStripe1DayApplicable = applicable;
  },
  SET_CARD_INFO(cardState, cardInfo) {
    cardState.cardInfo = {
      has_card: false,
      card_brand: '',
      last4: '',
      exp_month: null,
      exp_year: null,
      card_status: '',
      is_1day_plan_active: false,
      has_payment_receipts: false,
      ...cardInfo,
    };
  },
  SET_LOADING(cardState, loading) {
    cardState.loading = loading;
  },
  SET_ERROR(cardState, error) {
    cardState.error = error;
  },
  CLEAR_ERROR(cardState) {
    cardState.error = null;
  },
};

const actions = {
  async fetchStripeConfig({ commit, state: cardState }) {
    if (!cardState.stripeReleaseDate) {
      const { status, data } = await getStripeConfigApi();
      if (status === 200 && data) {
        if (data.publishable_key) {
          commit('SET_STRIPE_PUBLISHABLE_KEY', data.publishable_key);
        }
        if (data.stripe_release_date) {
          commit('SET_STRIPE_RELEASE_DATE', data.stripe_release_date);
        }
        if (typeof data.is_stripe_1day_applicable === 'boolean') {
          commit('SET_IS_STRIPE_1DAY_APPLICABLE', data.is_stripe_1day_applicable);
        }
      }
    }

    if (cardState.stripePublishableKey) {
      return cardState.stripePublishableKey;
    }

    const envKey = process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY;
    if (envKey) {
      commit('SET_STRIPE_PUBLISHABLE_KEY', envKey);
      return envKey;
    }

    throw new Error('stripe_config_unavailable');
  },

  async fetchStripeApplicability({ commit, dispatch, rootState, state: cardState }) {
    if (rootState.isAuthenticated
      && rootState.authUser
      && typeof rootState.authUser.is_stripe_1day_applicable === 'boolean') {
      commit('SET_IS_STRIPE_1DAY_APPLICABLE', rootState.authUser.is_stripe_1day_applicable);
      return rootState.authUser.is_stripe_1day_applicable;
    }

    if (rootState.isAuthenticated) {
      try {
        const { status, data } = await getCardInfoApi();
        if (status === 200 && data && typeof data.is_stripe_1day_applicable === 'boolean') {
          commit('SET_IS_STRIPE_1DAY_APPLICABLE', data.is_stripe_1day_applicable);
          if (data.has_card !== undefined) {
            commit('SET_CARD_INFO', data);
          }
          return data.is_stripe_1day_applicable;
        }
      } catch (err) {
        // Fall back to public stripe config for guest-like flows.
      }
    }

    await dispatch('fetchStripeConfig');
    if (typeof cardState.isStripe1DayApplicable === 'boolean') {
      return cardState.isStripe1DayApplicable;
    }

    const releaseDate = cardState.stripeReleaseDate;
    if (!releaseDate) {
      commit('SET_IS_STRIPE_1DAY_APPLICABLE', true);
      return true;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const release = new Date(`${releaseDate}T00:00:00`);
    const isApplicable = today >= release;
    commit('SET_IS_STRIPE_1DAY_APPLICABLE', isApplicable);
    return isApplicable;
  },

  async fetchCardInfo({ commit }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const { status, data } = await getCardInfoApi();
      if (status === 200 && data) {
        commit('SET_CARD_INFO', data);
        if (typeof data.is_stripe_1day_applicable === 'boolean') {
          commit('SET_IS_STRIPE_1DAY_APPLICABLE', data.is_stripe_1day_applicable);
        }
        return data;
      }
      throw new Error('card_info_fetch_failed');
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async createCardSetupIntent({ dispatch }, payload) {
    await dispatch('fetchStripeConfig');
    const { status, data } = await createCardSetupIntentApi(payload);
    if (status === 200 && data && data.client_secret) {
      return data;
    }
    const message = (data && data.message) || 'create_setup_intent_failed';
    throw new Error(message);
  },

  async createGuestCardSetupIntent({ dispatch }, payload) {
    await dispatch('fetchStripeConfig');
    const { status, data } = await createGuestCardSetupIntentApi(payload);
    if (status === 200 && data && data.client_secret) {
      return data;
    }
    const message = (data && data.message) || 'create_setup_intent_failed';
    throw new Error(message);
  },

  async registerCard({ commit, dispatch }, payload) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const { status, data } = await registerCardApi(payload);
      if (status === 200 && data && data.success) {
        if (data.card_info) {
          commit('SET_CARD_INFO', {
            has_card: true,
            card_brand: data.card_info.brand,
            last4: data.card_info.last4,
            exp_month: data.card_info.exp_month,
            exp_year: data.card_info.exp_year,
            card_status: data.card_info.status,
          });
        }
        try {
          await dispatch('fetchCardInfo');
        } catch (err) {
          // Register succeeded; refresh failure should not fail the mutation.
        }
        return data;
      }
      const message = (data && data.message) || 'register_card_failed';
      commit('SET_ERROR', message);
      throw new Error(message);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async deleteCard({ commit, dispatch }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const { status, data } = await deleteCardApi();
      if (status === 200 && data && data.success) {
        commit('SET_CARD_INFO', { has_card: false });
        try {
          await dispatch('fetchCardInfo');
        } catch (err) {
          // Delete succeeded; refresh failure should not fail the mutation.
        }
        return data;
      }
      const message = (data && data.message) || 'delete_card_failed';
      commit('SET_ERROR', message);
      throw new Error(message);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async finalize3dsReturn({ commit }, payload) {
    const paymentIntentId = typeof payload === 'string' ? payload : payload && payload.paymentIntentId;
    const paymentIntentClientSecret = typeof payload === 'object' && payload
      ? payload.paymentIntentClientSecret
      : null;
    if (!paymentIntentId) {
      return null;
    }
    commit('CLEAR_ERROR');
    const { status, data } = await finalize3dsReturnApi(
      paymentIntentId,
      paymentIntentClientSecret,
    );
    if (status === 200 && data) {
      return data;
    }
    const message = 'payment.three_ds_return.failed';
    commit('SET_ERROR', message);
    throw new Error(message);
  },

  async fetchReceiptList(_ctx, params) {
    const { status, data } = await getReceiptListApi(params);
    if (status === 200 && data) {
      return data;
    }
    throw new Error('payment.receipt_download.load_failed');
  },

  async downloadReceiptPdf(_ctx, receiptId) {
    const { status, data } = await downloadReceiptApi(receiptId);
    if (status === 200 && data && data.receipt_url) {
      return {
        type: 'url',
        receiptUrl: data.receipt_url,
      };
    }
    throw new Error('payment.receipt_download.download_failed');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
